import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import ObjectConfigApi from "../../api/ObjectConfigApi";
import PicklistFieldSelector from "./PicklistFieldSelector";
import LookupLayout from "../layout/LookupLayout";

function normalizeObjectOptions(objectConfigs) {
  if (!Array.isArray(objectConfigs)) return [];
  return objectConfigs
    .map((o) => ({
      name: o?.object_name,
      label: o?.object_label || o?.object_name,
    }))
    .filter((o) => o.name);
}

function mergeFieldNameFromValue(v) {
  if (typeof v !== "string" || !v.includes("{{")) return "";
  const match = v.match(/\{\{\s*([^}]+)\s*\}\}/);
  return match ? match[1].trim() : "";
}

function effectiveValueType(m) {
  if (m?.valueType === "field" || m?.valueType === "static") {
    return m.valueType;
  }
  if (typeof m?.value === "string" && m.value.includes("{{")) {
    return "field";
  }
  return "static";
}

/** Matches FieldManager / platform: omit from Create Record target field picker. */
const SKIP_TARGET_SYSTEM_FIELD_NAMES = new Set([
  "id",
  "createddate",
  "lastmodifieddate",
  "createdbyid",
  "lastmodifiedbyid",
  "is_deleted",
]);

/** Owner is often marked system but should remain assignable on create. */
const ALLOW_CREATE_TARGET_OWNER_NAMES = new Set(["ownerid", "owner_id"]);

function infoIcon(overlay, id) {
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip id={id}>{overlay}</Tooltip>}>
      <i className="fa fa-info-circle text-muted ms-1" role="img" aria-label="Help" style={{ cursor: "help" }} />
    </OverlayTrigger>
  );
}

function shouldOfferFieldAsCreateTarget(f) {
  if (!f?.field_name) return false;
  const name = String(f.field_name).toLowerCase();
  if (ALLOW_CREATE_TARGET_OWNER_NAMES.has(name)) return true;
  if (SKIP_TARGET_SYSTEM_FIELD_NAMES.has(name)) return false;
  const dt = (f.data_type || "").toLowerCase();
  if (dt === "autonumber") return false;
  if (dt === "calculated" || dt === "rollupsummary") return false;
  if (
    f.is_system === true ||
    f.is_system === "true" ||
    f.is_system === 1 ||
    f.is_system === "1"
  ) {
    return false;
  }
  return true;
}

function targetFieldDropdownOptions(
  allFields,
  selectableFields,
  currentFieldName
) {
  const names = new Set(selectableFields.map((f) => f.field_name));
  const list = [...selectableFields];
  if (currentFieldName && !names.has(currentFieldName)) {
    const legacy = allFields.find((f) => f.field_name === currentFieldName);
    if (legacy) list.unshift(legacy);
  }
  return list;
}

function getTargetFieldDef(fieldName, availableFields) {
  if (!fieldName || !Array.isArray(availableFields)) return null;
  return availableFields.find((f) => f.field_name === fieldName) || null;
}

function staticStringValue(m) {
  const v = m?.value;
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v);
}

function isPlainStaticValue(m) {
  const s = staticStringValue(m);
  return !s.includes("{{");
}

function renderStaticValueInput(idx, m, fieldDef, readOnly, mergeMappingRow) {
  if (!fieldDef) {
    return (
      <Form.Control
        type="text"
        value={isPlainStaticValue(m) ? staticStringValue(m) : ""}
        onChange={(e) =>
          mergeMappingRow(idx, {
            valueType: "static",
            value: e.target.value,
            staticLookupLabel: "",
          })
        }
        disabled={readOnly}
        placeholder="Static value"
      />
    );
  }

  const dt = (fieldDef.data_type || "").toLowerCase();

  const patchStatic = (value, extra = {}) =>
    mergeMappingRow(idx, {
      valueType: "static",
      value,
      staticLookupLabel: "",
      ...extra,
    });

  const patchStaticLookup = (id, label) =>
    mergeMappingRow(idx, {
      valueType: "static",
      value: id || "",
      staticLookupLabel: label || "",
    });

  if (dt === "picklist") {
    return (
      <PicklistFieldSelector
        field={fieldDef}
        value={isPlainStaticValue(m) ? staticStringValue(m) : ""}
        onChange={(v) => patchStatic(v)}
        disabled={readOnly}
        isMultiSelect={false}
      />
    );
  }

  if (dt === "multipicklist") {
    return (
      <PicklistFieldSelector
        field={fieldDef}
        value={isPlainStaticValue(m) ? staticStringValue(m) : ""}
        onChange={(v) => patchStatic(v)}
        disabled={readOnly}
        isMultiSelect={true}
      />
    );
  }

  if (dt === "lookup" || fieldDef.is_reference === true) {
    const refObj = fieldDef.reference_object || fieldDef.referenceObject;
    const displayFields =
      fieldDef.display_fields || fieldDef.displayFields || "name";
    const searchFields =
      fieldDef.lookup_search_fields ||
      fieldDef.lookupSearchFields ||
      displayFields;
    if (!refObj) {
      return (
        <Form.Text className="text-muted">
          This lookup has no reference object configured. Use a text value or
          switch to Field mapping.
        </Form.Text>
      );
    }
    // Do not pass field lookup_filter into LookupLayout here: workflow config UI
    // has no record context to resolve {{field}} placeholders, so filters/filterLogic
    // would hit the lookup API incorrectly. Users still search and pick by name/id.
    return (
      <LookupLayout
        objectName={refObj}
        searchFields={searchFields}
        displayFields={displayFields}
        value={
          isPlainStaticValue(m) && staticStringValue(m)
            ? staticStringValue(m)
            : ""
        }
        defaultLabel={m.staticLookupLabel || ""}
        onChange={(id, label) => patchStaticLookup(id, label)}
        disabled={readOnly}
        placeholder="Search record..."
      />
    );
  }

  if (dt === "boolean") {
    const selVal =
      m?.value === true ? "true" : m?.value === false ? "false" : "";
    return (
      <Form.Select
        value={selVal}
        onChange={(e) => {
          const v = e.target.value;
          patchStatic(v === "" ? "" : v === "true");
        }}
        disabled={readOnly}
      >
        <option value="">—</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </Form.Select>
    );
  }

  if (dt === "integer") {
    return (
      <Form.Control
        type="number"
        step={1}
        value={isPlainStaticValue(m) ? staticStringValue(m) : ""}
        onChange={(e) => patchStatic(e.target.value)}
        disabled={readOnly}
        placeholder="Whole number"
      />
    );
  }

  if (
    dt === "number" ||
    dt === "currency" ||
    dt === "percent" ||
    dt === "decimal" ||
    dt === "float"
  ) {
    return (
      <Form.Control
        type="number"
        step="any"
        value={isPlainStaticValue(m) ? staticStringValue(m) : ""}
        onChange={(e) => patchStatic(e.target.value)}
        disabled={readOnly}
        placeholder="Numeric value"
      />
    );
  }

  if (dt === "date") {
    return (
      <Form.Control
        type="date"
        value={isPlainStaticValue(m) ? staticStringValue(m).slice(0, 10) : ""}
        onChange={(e) => patchStatic(e.target.value)}
        disabled={readOnly}
      />
    );
  }

  if (dt === "datetime") {
    let v = isPlainStaticValue(m) ? staticStringValue(m) : "";
    if (v && v.includes("T") && v.length > 16) {
      v = v.slice(0, 16);
    }
    return (
      <Form.Control
        type="datetime-local"
        value={v}
        onChange={(e) => patchStatic(e.target.value)}
        disabled={readOnly}
      />
    );
  }

  // Time fields — same native picker as EditLayout
  if (dt === "time") {
    let v = isPlainStaticValue(m) ? staticStringValue(m) : "";
    if (v) {
      if (v.includes("T") || v.includes(" ")) {
        const d = new Date(v);
        if (!Number.isNaN(d.getTime())) {
          v =
            String(d.getHours()).padStart(2, "0") +
            ":" +
            String(d.getMinutes()).padStart(2, "0");
        }
      } else if (v.includes(":")) {
        const parts = v.split(":");
        v = parts[0] + ":" + parts[1];
      }
    }
    return (
      <Form.Control
        type="time"
        value={v}
        onChange={(e) => patchStatic(e.target.value)}
        disabled={readOnly}
      />
    );
  }

  if (dt === "textarea") {
    return (
      <Form.Control
        as="textarea"
        rows={2}
        value={isPlainStaticValue(m) ? staticStringValue(m) : ""}
        onChange={(e) => patchStatic(e.target.value)}
        disabled={readOnly}
        placeholder="Text"
      />
    );
  }

  const inputType =
    dt === "email"
      ? "email"
      : dt === "url"
        ? "url"
        : dt === "phone"
          ? "tel"
          : "text";

  return (
    <Form.Control
      type={inputType}
      value={isPlainStaticValue(m) ? staticStringValue(m) : ""}
      onChange={(e) => patchStatic(e.target.value)}
      disabled={readOnly}
      placeholder="Static value"
    />
  );
}

export default function WorkflowCreateRecord({
  config,
  onChange,
  objectConfigs,
  sourceObjectName,
  readOnly,
}) {
  const objectOptions = useMemo(
    () => normalizeObjectOptions(objectConfigs),
    [objectConfigs]
  );

  const [availableFields, setAvailableFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [sourceFields, setSourceFields] = useState([]);
  const [loadingSourceFields, setLoadingSourceFields] = useState(false);

  const targetObjectName = config?.targetObjectName || "";
  const fieldMappings = Array.isArray(config?.fieldMappings)
    ? config.fieldMappings
    : [];

  const targetSelectableFields = useMemo(
    () => availableFields.filter(shouldOfferFieldAsCreateTarget),
    [availableFields]
  );

  useEffect(() => {
    const loadFields = async () => {
      if (!targetObjectName) {
        setAvailableFields([]);
        return;
      }
      setLoadingFields(true);
      try {
        const res = await ObjectConfigApi.fetchConfigObjectByNameWithFields(
          targetObjectName
        );
        const fields = res?.success && res?.data?.fields ? res.data.fields : [];
        setAvailableFields(fields);
      } catch (e) {
        console.error("Failed to load fields for Create Record:", e);
        setAvailableFields([]);
      } finally {
        setLoadingFields(false);
      }
    };

    loadFields();
  }, [targetObjectName]);

  useEffect(() => {
    let cancelled = false;
    const loadSource = async () => {
      if (!sourceObjectName) {
        setSourceFields([]);
        return;
      }
      setLoadingSourceFields(true);
      try {
        const res = await ObjectConfigApi.fetchConfigObjectByNameWithFields(
          sourceObjectName
        );
        if (cancelled) return;
        const fields = res?.success && res?.data?.fields ? res.data.fields : [];
        const filtered = fields.filter((f) => {
          const d = (f.data_type || "").toLowerCase();
          return d !== "calculated" && d !== "rollupsummary";
        });
        setSourceFields(filtered);
      } catch (e) {
        if (!cancelled) setSourceFields([]);
      } finally {
        if (!cancelled) setLoadingSourceFields(false);
      }
    };
    loadSource();
    return () => {
      cancelled = true;
    };
  }, [sourceObjectName]);

  const updateMappings = (next) => onChange("fieldMappings", next);

  const mergeMappingRow = (idx, patch) => {
    const next = [...fieldMappings];
    next[idx] = { ...(next[idx] || {}), ...patch };
    updateMappings(next);
  };

  const addMappingRow = () => {
    updateMappings([
      ...fieldMappings,
      { fieldName: "", value: "", valueType: "static" },
    ]);
  };

  const deleteMappingRow = (idx) => {
    updateMappings(fieldMappings.filter((_, i) => i !== idx));
  };

  const handleTargetObjectChange = (e) => {
    const nextObject = e.target.value;
    onChange("targetObjectName", nextObject);
    onChange("fieldMappings", []);
  };

  const handleTargetFieldChange = (idx, fieldName) => {
    mergeMappingRow(idx, {
      fieldName,
      value: "",
      valueType: "static",
      staticLookupLabel: "",
    });
  };

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Create record in object</Form.Label>
        <Form.Select
          value={targetObjectName}
          onChange={handleTargetObjectChange}
          disabled={readOnly}
          required={!readOnly}
        >
          <option value="">-Select Object-</option>
          {objectOptions.map((o) => (
            <option key={o.name} value={o.name}>
              {o.label}
            </option>
          ))}
        </Form.Select>
        <Form.Text className="text-muted">
          Choose which object will receive the new record.
        </Form.Text>
      </Form.Group>

      <div className="mb-2 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center flex-wrap gap-1">
          <span className="fw-semibold">Field values</span>
          {infoIcon(
            <span className="text-start d-inline-block" style={{ maxWidth: 340 }}>
              Choose the <strong>target field</strong> on the new record. System-managed
              fields are hidden: <strong>id</strong>, audit columns (created/modified by and
              dates), <strong>autonumber</strong>, <strong>calculated</strong>, and{" "}
              <strong>rollup summary</strong>, plus other system-only fields.{" "}
              <strong>Owner</strong> can be set when shown in the list.
              <br />
              <br />
              <strong>Static</strong> — enter or pick a value; the control matches the target
              field type (picklist, lookup, number, date, etc.).
              <br />
              <strong>Field</strong> — copy a value from the record that triggered this
              workflow (same object as the workflow).
            </span>,
            "wf-create-record-fields-overview"
          )}
        </div>
        {!readOnly && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={addMappingRow}
            disabled={!targetObjectName}
            title={!targetObjectName ? "Select an object first" : ""}
          >
            Add Field
          </Button>
        )}
      </div>

      {loadingFields && targetObjectName ? (
        <div className="py-2">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading target fields...
        </div>
      ) : null}

      {fieldMappings.length === 0 ? (
        <div className="text-muted border rounded p-3">
          No field mappings yet.
        </div>
      ) : (
        <div className="border rounded p-3">
          {fieldMappings.map((m, idx) => {
            const hasTargetField = Boolean(
              m?.fieldName && String(m.fieldName).trim() !== ""
            );
            const vType = effectiveValueType(m);
            const fieldDef = getTargetFieldDef(m?.fieldName, availableFields);

            return (
              <Row className="g-2 align-items-start mb-3" key={idx}>
                <Col md={5}>
                  <Form.Label className="small text-muted mb-1 d-flex align-items-center">
                    Target field (new record)
                    {infoIcon(
                      "Only fields you can set on a new record appear here. Id, audit fields, autonumber, calculated, and rollup summary are omitted; owner is included when available.",
                      `wf-create-target-${idx}`
                    )}
                  </Form.Label>
                  <Form.Select
                    value={m?.fieldName || ""}
                    onChange={(e) =>
                      handleTargetFieldChange(idx, e.target.value)
                    }
                    disabled={readOnly}
                    required={!readOnly}
                  >
                    <option value="">-Select Field-</option>
                    {targetFieldDropdownOptions(
                      availableFields,
                      targetSelectableFields,
                      m?.fieldName
                    ).map((f) => (
                      <option key={f.field_name} value={f.field_name}>
                        {f.field_label || f.field_name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={6}>
                  {!hasTargetField ? (
                    <div className="text-muted small pt-4">
                      Select a target field first, then set the value.
                    </div>
                  ) : (
                    <>
                      <Form.Label className="small text-muted mb-1 d-flex align-items-center flex-wrap">
                        Value
                        {infoIcon(
                          "Static: typed control for this target field (picklist, lookup, number, date, etc.). Field: choose a column on the workflow record; its value is copied when the workflow runs (stored as {{fieldName}}).",
                          `wf-create-value-${idx}`
                        )}
                      </Form.Label>
                      <Row className="g-2">
                        <Col sm={4}>
                          <Form.Select
                            value={vType}
                            onChange={(e) => {
                              const nextType = e.target.value;
                              if (nextType === "static") {
                                mergeMappingRow(idx, {
                                  valueType: "static",
                                  value: "",
                                  staticLookupLabel: "",
                                });
                              } else {
                                mergeMappingRow(idx, {
                                  valueType: "field",
                                  value: "",
                                  staticLookupLabel: "",
                                });
                              }
                            }}
                            disabled={readOnly}
                          >
                            <option value="static">Static</option>
                            <option value="field" disabled={!sourceObjectName}>
                              Field
                              {!sourceObjectName
                                ? " (set workflow object first)"
                                : ""}
                            </option>
                          </Form.Select>
                        </Col>
                        <Col sm={8}>
                          {vType === "field" ? (
                            <>
                              {loadingSourceFields ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <Form.Select
                                  value={mergeFieldNameFromValue(m?.value)}
                                  onChange={(e) => {
                                    const fn = e.target.value;
                                    mergeMappingRow(idx, {
                                      valueType: "field",
                                      value: fn ? `{{${fn}}}` : "",
                                      staticLookupLabel: "",
                                    });
                                  }}
                                  disabled={
                                    readOnly ||
                                    !sourceObjectName ||
                                    sourceFields.length === 0
                                  }
                                  required={!readOnly}
                                >
                                  <option value="">
                                    -Select workflow field-
                                  </option>
                                  {sourceFields.map((f) => (
                                    <option
                                      key={f.field_name}
                                      value={f.field_name}
                                    >
                                      {f.field_label || f.field_name}
                                    </option>
                                  ))}
                                </Form.Select>
                              )}
                              <Form.Text className="text-muted">
                                Value copied from the record that triggered this
                                workflow.
                              </Form.Text>
                            </>
                          ) : (
                            renderStaticValueInput(
                              idx,
                              m,
                              fieldDef,
                              readOnly,
                              mergeMappingRow
                            )
                          )}
                        </Col>
                      </Row>
                    </>
                  )}
                </Col>
                <Col md={1} className="text-end pt-4">
                  {!readOnly && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => deleteMappingRow(idx)}
                      title="Remove"
                    >
                      <i className="fa fa-trash" />
                    </Button>
                  )}
                </Col>
              </Row>
            );
          })}
        </div>
      )}
    </>
  );
}
